import React, { useState } from 'react'
import { Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Button from '../components/ui/button'
import PageHeader from '../components/page-header'
import ConfirmDialog from '../components/ui/confirm-dialog'
import { useAllSeriesForCurrentUser, useDeleteSeries } from '../api/series'
import { SeriesCard } from '../components/series/series-card'
import { SeriesEmptyState } from '../components/series/series-empty-state'
import { track } from '../lib/analytics/posthog'

const SeriesPage: React.FC = () => {
  const navigate = useNavigate()
  const { data: seriesList = [], isLoading } = useAllSeriesForCurrentUser()
  const deleteSeries = useDeleteSeries()

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null; name: string }>({
    open: false,
    id: null,
    name: '',
  })

  React.useEffect(() => {
    track('series_pageview', { page: 'list', type: 'new-series' })
  }, [])

  const handleDeleteClick = (id: string) => {
    const s = seriesList.find((s) => s.id === id)
    setDeleteDialog({ open: true, id, name: s?.name || 'this series' })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.id) return
    await deleteSeries.mutateAsync(deleteDialog.id)
  }

  // Filter out deleted series
  const activeSeries = seriesList.filter((s) => s.status !== 'deleted')

  return (
    <div>
      <PageHeader
        title="Series"
        subtitle="Your automated, personalized podcast feeds"
        actions={
          <Button
            onClick={() => navigate('/series/new')}
            className="flex w-full items-center gap-2.5 shadow-sm transition-shadow duration-200 hover:shadow-md sm:w-auto"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New Series
          </Button>
        }
      />

      <div className="mt-8">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 xl:grid-cols-3" aria-busy="true">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse rounded-2xl border border-black/5 bg-white/60 p-6 backdrop-blur-sm">
                <div className="space-y-4">
                  <div className="h-4 w-3/4 rounded-lg bg-foreground/10" />
                  <div className="space-y-2">
                    <div className="h-3 w-full rounded bg-foreground/5" />
                    <div className="h-3 w-2/3 rounded bg-foreground/5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : activeSeries.length === 0 ? (
          <SeriesEmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 xl:grid-cols-3">
            {activeSeries.map((series) => (
              <SeriesCard
                key={series.id}
                series={series}
                onDelete={handleDeleteClick}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: open ? deleteDialog.id : null, name: deleteDialog.name })}
        title="Delete series?"
        description={`Are you sure you want to delete "${deleteDialog.name}"? All episodes will remain in your library, but no new episodes will be generated.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />
    </div>
  )
}

export default SeriesPage
